import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Target,
  CalendarCheck,
  Users,
  ShieldCheck,
  Smartphone,
  IndianRupee,
  BadgeCheck,
  Lock,
  Headset,
  Check,
  Plus,
  Home,
  MessageCircle,
  Quote,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Seo, OG_IMAGE_PARTNERS } from '@/seo/Seo';
import { breadcrumbSchema, faqSchema } from '@/seo/schemas';
import { configApi, type PartnerLanding as PartnerLandingData } from '@/api/configApi';
import { cn } from '@/lib/utils';

const ONBOARDING_PATH = '/partner-signup';

/** Content-icon keys (stored in Mongo) → lucide components. */
const ICONS: Record<string, LucideIcon> = {
  target: Target,
  'calendar-check': CalendarCheck,
  users: Users,
  'shield-check': ShieldCheck,
  smartphone: Smartphone,
  'indian-rupee': IndianRupee,
  'badge-check': BadgeCheck,
  lock: Lock,
  headset: Headset,
};

/** Segment accent keys → brand-token chip classes (theme-independent). */
const SEGMENT_ACCENT: Record<string, string> = {
  teal: 'bg-[var(--sc-teal-light)] text-[var(--sc-teal)]',
  amber: 'bg-[var(--sc-amber-light)] text-[var(--sc-amber)]',
  pink: 'bg-[var(--sc-pink-light)] text-[var(--sc-pink)]',
  violet: 'bg-[#EEEDFE] text-[#5B4FCF]',
  blue: 'bg-[var(--sc-blue-light)] text-[var(--sc-blue)]',
};

const CONTAINER = 'mx-auto w-full max-w-[1200px] px-5 sm:px-8 lg:px-12';

function Icon({ name, className }: { name: string; className?: string }) {
  const Cmp = ICONS[name] ?? Check;
  return <Cmp className={className} aria-hidden="true" />;
}

export default function PartnerLanding() {
  const navigate = useNavigate();
  const [data, setData] = useState<PartnerLandingData | null>(null);
  const [failed, setFailed] = useState(false);
  const [openFaq, setOpenFaq] = useState<number>(0);

  useEffect(() => {
    let active = true;
    configApi
      .getPartnerLanding()
      .then((res) => {
        if (active) setData(res.data.landing);
      })
      .catch(() => {
        // Backend already serves a default, so only a hard network error lands
        // here. Still surface a minimal page so the onboarding funnel is reachable.
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const goOnboard = () => navigate(ONBOARDING_PATH);

  // NOTE: every hook must run before any conditional return below, or React
  // will see a different hook count between renders once `failed`/`data` change.
  const partnerCount = data?.aggregates?.partner_count ?? 0;
  const seoFaqs = useMemo(
    () => (data?.faqs?.items ?? []).map((f) => ({ question: f.q, answer: f.a })),
    [data],
  );

  if (failed && !data) {
    return (
      <div className="bg-[var(--sc-teal-dark)] text-white">
        <Seo
          title="List Your Physiotherapy Clinic on Recovry | Partner With Us"
          description="Grow your physiotherapy practice with Recovry. Get discovered by patients actively searching for your services across PAN India. Free to join."
          canonical="/partners"
          ogTitle="Recovry – Reach More Patients, Grow Faster"
          ogDescription="Apply in 5 minutes. Start seeing patients."
          ogImage={OG_IMAGE_PARTNERS}
        />
        <div className={cn(CONTAINER, 'flex min-h-[70vh] flex-col items-center justify-center py-24 text-center')}>
          <h1 className="max-w-2xl text-3xl font-extrabold tracking-tight sm:text-4xl">
            Grow your physiotherapy practice with Recovry
          </h1>
          <p className="mt-4 max-w-lg text-base text-white/70">
            Get discovered by patients actively searching for your services across PAN India. Free to
            join, no listing or subscription fees.
          </p>
          <Button size="lg" className="mt-8 gap-2" onClick={goOnboard}>
            Start onboarding, it's free
            <ArrowRight className="size-4" />
          </Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center">
        <div className="size-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const { hero, stats, benefits, home_visit, segments, steps, testimonials, trust_badges, faqs, final_cta } = data;

  return (
    <div className="bg-background">
      <Seo
        title="List Your Physiotherapy Clinic on Recovry | Partner With Us"
        description="Grow your physiotherapy practice with Recovry. Get discovered by patients actively searching for your services across PAN India. Free to join, no listing or subscription fees."
        canonical="/partners"
        ogTitle="Recovry – Reach More Patients, Grow Faster"
        ogDescription="Apply in 5 minutes. Start seeing patients."
        ogImage={OG_IMAGE_PARTNERS}
        keywords="list physiotherapy clinic, partner with Recovry, physiotherapy clinic marketing, get more physio patients, register physio clinic India"
        schema={[
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'For Partners', path: '/partners' },
          ]),
          ...(seoFaqs.length ? [faqSchema(seoFaqs)] : []),
        ]}
      />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="bg-[var(--sc-teal-dark)] text-white">
        <div className={cn(CONTAINER, 'grid items-center gap-10 py-16 lg:grid-cols-2 lg:gap-16 lg:py-24')}>
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--sc-teal-mid)]">
              <span className="size-1.5 animate-pulse rounded-full bg-[var(--sc-teal-mid)]" />
              {hero.eyebrow}
            </span>
            <h1 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-[3.4rem]">
              {hero.title}
              <br />
              <span className="text-[var(--sc-teal-mid)]">{hero.title_highlight}</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
              {hero.subtitle}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button size="lg" className="gap-2 shadow-lg" onClick={goOnboard}>
                {hero.primary_cta}
                <ArrowRight className="size-4" />
              </Button>
              <a
                href="#how-it-works"
                className="text-sm font-semibold text-white/70 transition-colors hover:text-white"
              >
                {hero.secondary_cta} →
              </a>
            </div>
            <ul className="mt-9 flex flex-wrap gap-x-5 gap-y-2 border-t border-white/10 pt-6">
              {hero.trust_items.map((item) => (
                <li key={item} className="flex items-center gap-1.5 text-xs text-white/55">
                  <Check className="size-3.5 text-[var(--sc-teal-mid)]" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Hero visual + stat chips */}
          <div className="flex flex-col gap-4">
            <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/5">
              <img
                src={hero.image_url}
                alt="Physiotherapist treating a patient at a Recovry partner clinic"
                loading="eager"
                className="aspect-[16/10] w-full object-cover"
              />
              {hero.image_label ? (
                <span className="absolute left-3 top-3 rounded-full bg-[var(--sc-teal)]/90 px-2.5 py-1 text-[11px] font-semibold text-white">
                  {hero.image_label}
                </span>
              ) : null}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {hero.stat_chips.map((chip) => (
                <div key={chip.label} className="rounded-xl border border-white/10 bg-white/5 p-4">
                  <div className="text-2xl font-extrabold leading-none text-[var(--sc-amber)]">
                    {chip.value}
                  </div>
                  <div className="mt-1.5 text-[11.5px] leading-snug text-white/55">{chip.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAND ───────────────────────────────────────── */}
      <section className="bg-[var(--sc-ink)] text-white">
        <div className={cn(CONTAINER, 'py-14 lg:py-16')}>
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-[var(--sc-amber)]">
              {stats.label}
            </p>
            <h2 className="mt-3 text-2xl font-extrabold tracking-tight sm:text-3xl">{stats.title}</h2>
            <p className="mt-3 text-sm text-white/55 sm:text-base">{stats.subtitle}</p>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-2xl lg:grid-cols-4">
            {stats.items.map((s) => (
              <div key={s.label} className="border border-white/10 bg-white/[0.04] p-7 text-center">
                <div className="text-5xl font-black tracking-tight text-[var(--sc-amber)] lg:text-6xl">
                  {s.value}
                  {s.unit ? <span className="align-super text-2xl">{s.unit}</span> : null}
                </div>
                <div className="mt-3 text-[13px] leading-snug text-white/55">{s.label}</div>
                {s.source ? (
                  <div className="mt-1.5 text-[11px] italic text-white/30">{s.source}</div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BENEFITS ─────────────────────────────────────────── */}
      <section className={cn(CONTAINER, 'py-16 lg:py-24')}>
        <SectionHeading label={benefits.label} title={benefits.title} subtitle={benefits.subtitle} />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {benefits.items.map((b) => (
            <article
              key={b.title}
              className={cn(
                'group rounded-2xl border p-6 transition-all',
                b.featured
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card hover:border-[var(--sc-teal-mid)] hover:shadow-lg hover:shadow-primary/5',
              )}
            >
              <div
                className={cn(
                  'flex size-12 items-center justify-center rounded-xl',
                  b.featured ? 'bg-white/15' : 'bg-secondary',
                )}
              >
                <Icon name={b.icon} className={cn('size-6', b.featured ? 'text-white' : 'text-primary')} />
              </div>
              <h3 className="mt-5 text-base font-bold leading-snug">{b.title}</h3>
              <p className={cn('mt-2.5 text-sm leading-relaxed', b.featured ? 'text-white/75' : 'text-muted-foreground')}>
                {b.body}
              </p>
              {b.tag ? (
                <span
                  className={cn(
                    'mt-4 inline-block rounded-full px-2.5 py-1 text-[11px] font-bold',
                    b.featured ? 'bg-white/15 text-white' : 'bg-secondary text-primary',
                  )}
                >
                  {b.tag}
                </span>
              ) : null}
            </article>
          ))}
        </div>
      </section>

      {/* ── HOME VISIT CALLOUT ───────────────────────────────── */}
      <section className="border-y-[3px] border-[var(--sc-amber)] bg-[var(--sc-amber-light)]">
        <div className={cn(CONTAINER, 'flex flex-col items-start gap-6 py-12 md:flex-row md:items-center md:gap-8')}>
          <div className="flex size-16 shrink-0 items-center justify-center rounded-full bg-[var(--sc-amber)] text-white">
            <Home className="size-7" />
          </div>
          <div className="flex-1">
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8B5E00]">{home_visit.label}</p>
            <h3 className="mt-1.5 text-xl font-extrabold text-[var(--sc-ink)] sm:text-2xl">{home_visit.title}</h3>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[var(--sc-ink)]/80 sm:text-[15px]">
              {home_visit.body}
            </p>
          </div>
          <Button
            className="shrink-0 bg-[var(--sc-ink)] text-white hover:bg-[var(--sc-teal-dark)]"
            onClick={goOnboard}
          >
            {home_visit.cta}
          </Button>
        </div>
      </section>

      {/* ── AUDIENCE SEGMENTS ────────────────────────────────── */}
      <section className="bg-muted/40">
        <div className={cn(CONTAINER, 'py-16 lg:py-24')}>
          <SectionHeading label={segments.label} title={segments.title} subtitle={segments.subtitle} />
          <div className="mt-12 grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div className="overflow-hidden rounded-2xl border border-border">
              <img
                src={segments.image_url}
                alt="Diverse patients Recovry connects to physiotherapy clinics"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
            <ul className="flex flex-col gap-3.5">
              {segments.items.map((seg) => (
                <li
                  key={seg.title}
                  className="flex items-start gap-4 rounded-xl border border-border bg-card p-4 transition-colors hover:border-[var(--sc-teal-mid)]"
                >
                  <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-lg text-lg', SEGMENT_ACCENT[seg.color] ?? SEGMENT_ACCENT.teal)}>
                    {seg.emoji}
                  </span>
                  <div>
                    <h4 className="text-sm font-bold text-foreground">{seg.title}</h4>
                    <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">{seg.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────── */}
      <section id="how-it-works" className={cn(CONTAINER, 'py-16 lg:py-24')}>
        <SectionHeading label={steps.label} title={steps.title} subtitle={steps.subtitle} />
        <div className="relative mt-12 grid gap-10 md:grid-cols-3">
          <div className="absolute left-[16.66%] right-[16.66%] top-8 hidden h-px bg-border md:block" />
          {steps.items.map((step, i) => (
            <div key={step.title} className="relative text-center">
              <div className="mx-auto flex size-16 items-center justify-center rounded-full border-4 border-background bg-primary text-xl font-extrabold text-primary-foreground shadow-[0_0_0_1px_var(--border)]">
                {i + 1}
              </div>
              <h3 className="mt-6 text-base font-bold text-foreground">{step.title}</h3>
              <p className="mx-auto mt-2.5 max-w-xs text-sm leading-relaxed text-muted-foreground">{step.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <Button size="lg" className="gap-2" onClick={goOnboard}>
            Start onboarding
            <ArrowRight className="size-4" />
          </Button>
        </div>
      </section>

      {/* ── TESTIMONIALS ─────────────────────────────────────── */}
      <section className="bg-[var(--sc-teal-dark)] text-white">
        <div className={cn(CONTAINER, 'py-16 lg:py-24')}>
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-[var(--sc-teal-mid)]">
            {testimonials.label}
          </p>
          <h2 className="mt-3 max-w-2xl text-2xl font-extrabold tracking-tight sm:text-3xl">
            {testimonials.title}
          </h2>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {testimonials.items.map((t) => (
              <figure key={t.name} className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.06] p-6">
                <Quote className="size-7 text-[var(--sc-teal-mid)]" />
                <blockquote className="mt-3.5 flex-1 text-[15px] leading-relaxed text-white/75">{t.text}</blockquote>
                {t.stat ? (
                  <div className="mt-5 rounded-lg border border-[var(--sc-amber)]/30 bg-[var(--sc-amber)]/15 px-3.5 py-2.5 text-[13px] font-semibold text-[var(--sc-amber)]">
                    {t.stat}
                  </div>
                ) : null}
                <figcaption className="mt-5 flex items-center gap-3">
                  <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-sm font-bold text-[var(--sc-teal-mid)]">
                    {t.initials}
                  </span>
                  <div>
                    <div className="text-sm font-bold text-white">{t.name}</div>
                    <div className="mt-0.5 text-xs text-white/45">{t.title}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRUST BADGES ─────────────────────────────────────── */}
      <section className="border-b border-border">
        <div className={cn(CONTAINER, 'py-12')}>
          <p className="text-center text-[11.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
            Why practitioners trust Recovry
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {trust_badges.map((badge) => (
              <div key={badge.label} className="flex items-center gap-2 text-sm font-semibold text-foreground/80">
                <Icon name={badge.icon} className="size-5 text-primary" />
                {badge.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────── */}
      <section className={cn(CONTAINER, 'py-16 lg:py-24')}>
        <SectionHeading label={faqs.label} title={faqs.title} />
        <div className="mt-10 max-w-3xl">
          {faqs.items.map((faq, i) => {
            const open = openFaq === i;
            return (
              <div key={faq.q} className="border-t border-border last:border-b">
                <button
                  type="button"
                  onClick={() => setOpenFaq(open ? -1 : i)}
                  aria-expanded={open}
                  className="flex w-full items-center justify-between gap-4 py-5 text-left"
                >
                  <span className={cn('text-base font-bold transition-colors', open ? 'text-primary' : 'text-foreground')}>
                    {faq.q}
                  </span>
                  <span
                    className={cn(
                      'flex size-7 shrink-0 items-center justify-center rounded-full transition-all',
                      open ? 'rotate-45 bg-primary text-primary-foreground' : 'bg-secondary text-primary',
                    )}
                  >
                    <Plus className="size-4" />
                  </span>
                </button>
                <div
                  className={cn(
                    'grid overflow-hidden text-[14.5px] leading-relaxed text-muted-foreground transition-all duration-300',
                    open ? 'grid-rows-[1fr] pb-5' : 'grid-rows-[0fr]',
                  )}
                >
                  <div className="min-h-0">{faq.a}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="pointer-events-none absolute -right-16 -top-16 size-72 rounded-full bg-white/[0.05]" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 size-60 rounded-full bg-white/[0.04]" />
        <div className={cn(CONTAINER, 'relative py-16 text-center lg:py-24')}>
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">{final_cta.title}</h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-primary-foreground/70 sm:text-lg">
            {partnerCount > 0
              ? `Join ${partnerCount}+ physiotherapy practices already receiving patients through Recovry across PAN India.`
              : final_cta.subtitle}
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <Button
              size="lg"
              variant="secondary"
              className="gap-2 bg-white text-primary hover:bg-white/90"
              onClick={goOnboard}
            >
              <ArrowRight className="size-4" />
              Start onboarding, it's free
            </Button>
            {final_cta.whatsapp ? (
              <a
                href={`https://wa.me/${final_cta.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary-foreground/75 transition-colors hover:text-primary-foreground"
              >
                <MessageCircle className="size-4" />
                Ask us on WhatsApp
              </a>
            ) : null}
          </div>
          <p className="mt-5 text-xs text-primary-foreground/45">{final_cta.note}</p>
        </div>
      </section>
    </div>
  );
}

function SectionHeading({ label, title, subtitle }: { label: string; title: string; subtitle?: string }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.1em] text-primary">{label}</p>
      <h2 className="mt-3 max-w-2xl text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
        {title}
      </h2>
      {subtitle ? <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">{subtitle}</p> : null}
    </div>
  );
}
